/**
 * Christa 2026-09-25 (feedback 9725dd36): when a line bills less than its item, she must say which she means.
 * "The item is now $1,250" (nothing left to bill) or "Bill $1,250 now, $250 stays to bill later" (progress billing).
 * Nothing is preselected: a quick-added $10,000 retainer lowered to $2,000 usually means "bill $2,000 now", while her
 * $1,500 fee lowered to $1,250 meant "the fee is $1,250". Only she knows which.
 *
 * The server's rule (models/invoice_items.update_invoice_item, lower_billable_item_total): the new item total is this
 * line plus what the other live (non-cancelled) invoices bill on the item. These helpers mirror it for the labels.
 */

export type LowerChoice = 'lower' | 'keep';

export interface OtherBilling {
  invoice_number?: string | null;
  billed_amount?: number | null;
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function formatDollars(value: number): string {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * What the other live invoices bill on the item, or null when that is not known exactly (an invoice is counted but
 * its amount was not sent). `otherCount` is how many other live invoices carry the item.
 */
export function otherInvoicesBilled(others: OtherBilling[] | undefined | null, otherCount: number): number | null {
  if (otherCount <= 0) return 0;
  const list = others ?? [];
  if (list.length < otherCount) return null;
  let sum = 0;
  for (const other of list) {
    if (typeof other.billed_amount !== 'number' || !Number.isFinite(other.billed_amount)) return null;
    sum += other.billed_amount;
  }
  return round2(sum);
}

export interface LowerOutcome {
  /** The item total if she picks "the item is now ..." (a floor when not exact). */
  newTotal: number;
  exact: boolean;
  /** What stays to bill later if she picks "bill ... now" (null when not exact). */
  remainder: number | null;
}

export function lowerOutcome(lineAmount: number, itemTotal: number, otherBilled: number | null): LowerOutcome {
  if (otherBilled === null) return { newTotal: round2(lineAmount), exact: false, remainder: null };
  const newTotal = round2(lineAmount + otherBilled);
  return { newTotal, exact: true, remainder: round2(itemTotal - newTotal) };
}

/** The two options, in plain words with real dollar amounts. */
export function lowerChoiceLabels(lineAmount: number, outcome: LowerOutcome): Record<LowerChoice, string> {
  return {
    lower: outcome.exact
      ? `The item is now ${formatDollars(outcome.newTotal)} (nothing left to bill)`
      : `The item is now at least ${formatDollars(outcome.newTotal)} (nothing left to bill)`,
    keep: outcome.remainder !== null
      ? `Bill ${formatDollars(lineAmount)} now, ${formatDollars(outcome.remainder)} stays to bill later`
      : `Bill ${formatDollars(lineAmount)} now, the rest stays to bill later`,
  };
}

/** "0742", "0742 and 0755", "0742, 0755 and 0760". Null when any number is missing. */
export function joinInvoiceNumbers(others: OtherBilling[]): string | null {
  const numbers = others.map((other) => other.invoice_number);
  if (numbers.length === 0 || numbers.some((n) => !n)) return null;
  const named = numbers as string[];
  return named.length === 1 ? named[0] : `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`;
}
